import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, DeleteOutline, Download } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import {
  downloadAuthenticatedPdf,
  openAuthenticatedPdfViaToken,
  viewAuthenticatedPdf
} from '../../utils/authenticatedPdf';
import {
  formatAircraftTypeLine,
  formatApprovedDateLabel,
  getAircraftHoursFromAc,
  getAircraftIdFromWorkOrder,
  getAircraftLandingsFromAc,
  mergeEngineAssemblyGapFill,
  mergeTlbCarriedForwardIntoForm,
  pickLatestApprovedTlb
} from './mroWorkbenchFormAutofill';
import { getWorkOrderDisplayNumber } from '../../utils/workOrderDisplayNumber';

const FORM_TITLE = 'ACTION TAKEN FORM';
const SERIALIZED_RECORD_TITLE =
  'SERIALIZED COMPONENT REMOVAL/ INSTALLATION RECORD (*Additional sheet or same format may be used — if necessary)';
const REGULATORY_TRACKING_HEADER =
  "CA Form 1 / EASA Form 1 / FAA 8130-3 / 'S' Tag Tracking No. & Date";
const CRS_TEXT =
  'CRS certifies that the work specified except as otherwise specified was carried out in accordance with latest CAR-145 and in respect of the aircraft is considered ready for service.';

const EMPTY_SERIALIZED_COMPONENT_ROW = {
  description: '',
  offPartNumber: '',
  offSerialNumber: '',
  onPartNumber: '',
  onSerialNumber: '',
  regulatoryTracking: ''
};

const DEFAULT_SERIALIZED_RECORD_ROWS = 7;

export function emptySerializedComponentRows(count = DEFAULT_SERIALIZED_RECORD_ROWS) {
  return Array.from({ length: count }, () => ({ ...EMPTY_SERIALIZED_COMPONENT_ROW }));
}

export function normalizeSerializedComponentRecords(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return emptySerializedComponentRows();
  }
  return rows.map((r) => ({
    description: String(r?.description ?? '').trim(),
    offPartNumber: String(r?.offPartNumber ?? r?.offPn ?? '').trim(),
    offSerialNumber: String(r?.offSerialNumber ?? r?.offSn ?? '').trim(),
    onPartNumber: String(r?.onPartNumber ?? r?.onPn ?? '').trim(),
    onSerialNumber: String(r?.onSerialNumber ?? r?.onSn ?? '').trim(),
    regulatoryTracking: String(r?.regulatoryTracking ?? r?.trackingInfo ?? '').trim()
  }));
}

const SPARES_CONSUMABLES_TITLE =
  '*DETAILS OF SPARES / CONSUMABLES USED FOR THE JOB (*Additional sheet or same format may be used — if necessary)';

const EMPTY_SPARES_CONSUMABLES_ROW = {
  spareDescription: '',
  sparePartNumber: '',
  spareQty: '',
  spareExpiry: '',
  consumableDescription: '',
  consumablePartNumber: '',
  consumableExpiry: ''
};

const DEFAULT_SPARES_CONSUMABLES_ROWS = 13;

export function emptySparesConsumablesRows(count = DEFAULT_SPARES_CONSUMABLES_ROWS) {
  return Array.from({ length: count }, () => ({ ...EMPTY_SPARES_CONSUMABLES_ROW }));
}

export function normalizeSparesConsumablesRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return emptySparesConsumablesRows();
  }
  return rows.map((r) => ({
    spareDescription: String(r?.spareDescription ?? r?.spare?.description ?? '').trim(),
    sparePartNumber: String(r?.sparePartNumber ?? r?.spare?.partNumber ?? '').trim(),
    spareQty: String(r?.spareQty ?? r?.spare?.qty ?? '').trim(),
    spareExpiry: String(r?.spareExpiry ?? r?.spare?.expiry ?? '').trim(),
    consumableDescription: String(r?.consumableDescription ?? r?.consumable?.description ?? '').trim(),
    consumablePartNumber: String(r?.consumablePartNumber ?? r?.consumable?.partNumber ?? '').trim(),
    consumableExpiry: String(r?.consumableExpiry ?? r?.consumable?.expiry ?? '').trim()
  }));
}

const TOOLS_SPECIAL_TITLE =
  '*DETAILS OF TOOLS/SPECIAL TOOLS USED FOR THE JOB (*Additional sheet or same format may be used — if necessary)';

const EMPTY_TOOLS_SPECIAL_ROW = {
  description: '',
  identificationNo: '',
  range: '',
  calibrationError: '',
  calibrationValidity: ''
};

const DEFAULT_TOOLS_SPECIAL_ROWS = 10;

export function emptyToolsSpecialRows(count = DEFAULT_TOOLS_SPECIAL_ROWS) {
  return Array.from({ length: count }, () => ({ ...EMPTY_TOOLS_SPECIAL_ROW }));
}

export function normalizeToolsSpecialRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return emptyToolsSpecialRows();
  }
  return rows.map((r) => ({
    description: String(r?.description ?? '').trim(),
    identificationNo: String(r?.identificationNo ?? r?.identificationNumber ?? r?.partOrSerialNo ?? '').trim(),
    range: String(r?.range ?? '').trim(),
    calibrationError: String(r?.calibrationError ?? '').trim(),
    calibrationValidity: String(r?.calibrationValidity ?? r?.calibrationValidUntil ?? '').trim()
  }));
}

function formatDateInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

export function buildWorkOrderSecondPageInitialForm(workOrder, user) {
  const ac = workOrder?.aircraftId && typeof workOrder.aircraftId === 'object' ? workOrder.aircraftId : {};
  const today = new Date().toISOString().slice(0, 10);
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();

  return {
    formNumber: '',
    operatorName: '',
    aircraftType: formatAircraftTypeLine(ac),
    aircraftRegistration: (ac.registration && String(ac.registration).trim()) || '',
    aircraftSerialNumber: (ac.serialNumber && String(ac.serialNumber).trim()) || '',
    entryDate: today,
    maintenanceDetails: workOrder?.description || workOrder?.title || '',
    workOrderNumber: getWorkOrderDisplayNumber(workOrder) || workOrder?.title || '',
    workOrderDate: formatDateInput(workOrder?.woDate || workOrder?.scheduledDate || workOrder?.dueDate),
    crsNumber: '',
    crsDate: today,
    amoName: '',
    aircraftHours: getAircraftHoursFromAc(ac),
    aircraftLandings: getAircraftLandingsFromAc(ac),
    engine1Serial: '',
    engine2Serial: '',
    engine1Hours: '',
    engine2Hours: '',
    lhEngineCyclesCT: '',
    lhEngineCyclesPT: '',
    lhEngineCyclesIMP: '',
    rhEngineCyclesCT: '',
    rhEngineCyclesPT: '',
    rhEngineCyclesIMP: '',
    partsAndMaterialsUsed: '',
    toolsUsed: '',
    ameManHours: '',
    techManHours: '',
    workAccomplished: workOrder?.description || '',
    remarks: '',
    performedByName: displayName,
    performedByAuth: user?.authorityNumber ? String(user.authorityNumber) : '',
    performedByDate: today,
    performedByPlace: '',
    inspectedByName: '',
    inspectedByAuth: '',
    inspectedByDate: today,
    serializedComponentRecords: emptySerializedComponentRows(),
    sparesConsumablesRows: emptySparesConsumablesRows(),
    toolsSpecialRows: emptyToolsSpecialRows()
  };
}

const cellFieldSx = {
  '& .MuiInputBase-root': { fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.75, px: 0.75 }
};

/**
 * Action Taken Form — MRO engineer workbench.
 */
const WorkOrderSecondPageDialog = ({ open, onClose, workOrder, user, onSaved }) => {
  const { showToast } = useToast();
  const [form, setForm] = useState(() => buildWorkOrderSecondPageInitialForm(null, user));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingCrsPdf, setDownloadingCrsPdf] = useState(false);
  const [viewingPdf, setViewingPdf] = useState(false);
  const [formSavedAt, setFormSavedAt] = useState(null);
  const [tlbAutofillMeta, setTlbAutofillMeta] = useState(null);

  const woId = workOrder?._id;

  const tlbAutofillNote = useMemo(() => {
    if (!tlbAutofillMeta?.applied) return '';
    const parts = [];
    if (tlbAutofillMeta.sheetNumber) parts.push(`sheet ${tlbAutofillMeta.sheetNumber}`);
    if (tlbAutofillMeta.approvedDate) parts.push(`approved ${tlbAutofillMeta.approvedDate}`);
    const suffix = parts.length ? ` (${parts.join(', ')})` : '';
    return `Usage totals prefilled from the latest approved Technical Logbook${suffix}.`;
  }, [tlbAutofillMeta]);

  useEffect(() => {
    if (!open) {
      setTlbAutofillMeta(null);
      setFormSavedAt(null);
    }
  }, [open]);

  const loadForm = useCallback(async () => {
    if (!workOrder?._id) return;
    setLoading(true);
    setTlbAutofillMeta(null);
    try {
      let next = buildWorkOrderSecondPageInitialForm(workOrder, user);
      const [savedRes, tlbRes, asmRes] = await Promise.all([
        axios.get(`/api/maintenance/${workOrder._id}/work-order-second-page`).catch(() => ({ data: {} })),
        (async () => {
          const aircraftId = getAircraftIdFromWorkOrder(workOrder);
          if (!aircraftId) return { data: [] };
          return axios.get(`/api/technical-log-book/aircraft/${aircraftId}`).catch(() => ({ data: [] }));
        })(),
        (async () => {
          const aircraftId = getAircraftIdFromWorkOrder(workOrder);
          if (!aircraftId) return { data: { assemblies: [] } };
          return axios
            .get('/api/assemblies', { params: { aircraftId, pageSize: 500, page: 1, isActive: 'true' } })
            .catch(() => ({ data: { assemblies: [] } }));
        })()
      ]);

      const savedForm = savedRes.data?.form;
      setFormSavedAt(savedRes.data?.savedAt || null);
      if (savedForm && typeof savedForm === 'object' && Object.keys(savedForm).length > 0) {
        next = {
          ...next,
          ...savedForm,
          serializedComponentRecords: normalizeSerializedComponentRecords(savedForm.serializedComponentRecords),
          sparesConsumablesRows: normalizeSparesConsumablesRows(savedForm.sparesConsumablesRows),
          toolsSpecialRows: normalizeToolsSpecialRows(savedForm.toolsSpecialRows)
        };
      }

      const picked = pickLatestApprovedTlb(tlbRes.data);
      if (picked?.entry) {
        setTlbAutofillMeta({
          applied: true,
          sheetNumber: (picked.entry.sheetNumber && String(picked.entry.sheetNumber).trim()) || '',
          approvedDate: formatApprovedDateLabel(
            picked.entry.approvedAt || picked.entry.updatedAt || picked.entry.createdAt
          )
        });
        if (!savedForm || Object.keys(savedForm).length === 0) {
          next = mergeTlbCarriedForwardIntoForm(next, picked.totalCarriedForward);
          if (picked.entry.operatorName) next.operatorName = String(picked.entry.operatorName).trim();
          if (picked.entry.workOrderNumber) next.workOrderNumber = String(picked.entry.workOrderNumber).trim();
          if (picked.entry.workOrderDate) next.workOrderDate = formatDateInput(picked.entry.workOrderDate);
          if (picked.entry.crsNumber) next.crsNumber = String(picked.entry.crsNumber).trim();
          if (picked.entry.crsDate) next.crsDate = formatDateInput(picked.entry.crsDate);
          if (picked.entry.amoName) next.amoName = String(picked.entry.amoName).trim();
          if (picked.entry.maintenanceDetails) {
            next.maintenanceDetails = String(picked.entry.maintenanceDetails).trim();
          }
        }
      }

      const raw = asmRes.data?.assemblies ?? asmRes.data;
      const list = Array.isArray(raw) ? raw : [];
      if (!savedForm || Object.keys(savedForm).length === 0) {
        next = mergeEngineAssemblyGapFill(next, list);
      }

      setForm(next);
    } finally {
      setLoading(false);
    }
  }, [workOrder, user]);

  useEffect(() => {
    if (open && woId) loadForm();
  }, [open, woId, loadForm]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const serializedRows = normalizeSerializedComponentRecords(form.serializedComponentRecords);

  const setSerializedRow = (index, field, value) => {
    setForm((prev) => {
      const rows = normalizeSerializedComponentRecords(prev.serializedComponentRecords);
      const updated = [...rows];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, serializedComponentRecords: updated };
    });
  };

  const addSerializedRow = () => {
    setForm((prev) => ({
      ...prev,
      serializedComponentRecords: [
        ...normalizeSerializedComponentRecords(prev.serializedComponentRecords),
        { ...EMPTY_SERIALIZED_COMPONENT_ROW }
      ]
    }));
  };

  const removeSerializedRow = (index) => {
    setForm((prev) => {
      const rows = normalizeSerializedComponentRecords(prev.serializedComponentRecords);
      if (rows.length <= 1) return prev;
      return { ...prev, serializedComponentRecords: rows.filter((_, i) => i !== index) };
    });
  };

  const sparesConsumablesRows = normalizeSparesConsumablesRows(form.sparesConsumablesRows);

  const setSparesConsumablesRow = (index, field, value) => {
    setForm((prev) => {
      const rows = normalizeSparesConsumablesRows(prev.sparesConsumablesRows);
      const updated = [...rows];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, sparesConsumablesRows: updated };
    });
  };

  const addSparesConsumablesRow = () => {
    setForm((prev) => ({
      ...prev,
      sparesConsumablesRows: [
        ...normalizeSparesConsumablesRows(prev.sparesConsumablesRows),
        { ...EMPTY_SPARES_CONSUMABLES_ROW }
      ]
    }));
  };

  const removeSparesConsumablesRow = (index) => {
    setForm((prev) => {
      const rows = normalizeSparesConsumablesRows(prev.sparesConsumablesRows);
      if (rows.length <= 1) return prev;
      return { ...prev, sparesConsumablesRows: rows.filter((_, i) => i !== index) };
    });
  };

  const toolsSpecialRows = normalizeToolsSpecialRows(form.toolsSpecialRows);

  const setToolsSpecialRow = (index, field, value) => {
    setForm((prev) => {
      const rows = normalizeToolsSpecialRows(prev.toolsSpecialRows);
      const updated = [...rows];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, toolsSpecialRows: updated };
    });
  };

  const addToolsSpecialRow = () => {
    setForm((prev) => ({
      ...prev,
      toolsSpecialRows: [
        ...normalizeToolsSpecialRows(prev.toolsSpecialRows),
        { ...EMPTY_TOOLS_SPECIAL_ROW }
      ]
    }));
  };

  const removeToolsSpecialRow = (index) => {
    setForm((prev) => {
      const rows = normalizeToolsSpecialRows(prev.toolsSpecialRows);
      if (rows.length <= 1) return prev;
      return { ...prev, toolsSpecialRows: rows.filter((_, i) => i !== index) };
    });
  };

  const title = useMemo(
    () =>
      workOrder
        ? `Action Taken Form — ${getWorkOrderDisplayNumber(workOrder) || workOrder.title || workOrder._id}`
        : 'Action Taken Form',
    [workOrder]
  );

  const pdfFilename = useMemo(
    () =>
      `Action-Taken-Form_${(getWorkOrderDisplayNumber(workOrder) || workOrder?.title || workOrder?._id || 'work-order')
        .toString()
        .replace(/[^a-zA-Z0-9._-]+/g, '_')}.pdf`,
    [workOrder]
  );

  const crsPdfFilename = useMemo(
    () =>
      `Certificate-of-Release-to-Service_${(getWorkOrderDisplayNumber(workOrder) || workOrder?.title || workOrder?._id || 'work-order')
        .toString()
        .replace(/[^a-zA-Z0-9._-]+/g, '_')}.pdf`,
    [workOrder]
  );

  const handleSave = async () => {
    if (!workOrder?._id) return;
    setSaving(true);
    try {
      const res = await axios.post(`/api/maintenance/${workOrder._id}/work-order-second-page/save`, { form });
      const savedAt = res.data?.savedAt || new Date().toISOString();
      setFormSavedAt(savedAt);
      showToast('Action Taken Form saved', 'success');
      if (typeof onSaved === 'function') {
        await onSaved({ savedAt, workOrderId: workOrder._id });
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save Action Taken Form', 'error');
    } finally {
      setSaving(false);
    }
  };

  const actionTakenPdfUrl = workOrder?._id
    ? `/api/maintenance/${workOrder._id}/action-taken-form.pdf`
    : null;

  const crsPdfUrl = workOrder?._id ? `/api/maintenance/${workOrder._id}/crs.pdf` : null;

  const pdfBusy = saving || loading || downloadingPdf || downloadingCrsPdf || viewingPdf;

  const handleViewPdf = async () => {
    if (!actionTakenPdfUrl) return;
    if (!formSavedAt) {
      showToast('Save the Action Taken Form before viewing the PDF', 'warning');
      return;
    }
    setViewingPdf(true);
    try {
      await viewAuthenticatedPdf(actionTakenPdfUrl);
    } catch (err) {
      try {
        openAuthenticatedPdfViaToken(actionTakenPdfUrl);
      } catch (fallbackErr) {
        showToast(err.message || fallbackErr.message || 'Failed to open Action Taken Form PDF', 'error');
      }
    } finally {
      setViewingPdf(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!actionTakenPdfUrl) return;
    if (!formSavedAt) {
      showToast('Save the Action Taken Form before downloading PDF', 'warning');
      return;
    }
    setDownloadingPdf(true);
    try {
      await downloadAuthenticatedPdf(`${actionTakenPdfUrl}?download=1`, pdfFilename);
    } catch (err) {
      showToast(err.message || 'Failed to download Action Taken Form PDF', 'error');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadCrsPdf = async () => {
    if (!crsPdfUrl) return;
    if (!formSavedAt) {
      showToast('Save the Action Taken Form before downloading CRS', 'warning');
      return;
    }
    setDownloadingCrsPdf(true);
    try {
      await downloadAuthenticatedPdf(`${crsPdfUrl}?download=1`, crsPdfFilename);
    } catch (err) {
      showToast(err.message || 'Failed to download CRS PDF', 'error');
    } finally {
      setDownloadingCrsPdf(false);
    }
  };

  if (!workOrder) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <TextField
            fullWidth
            size="small"
            label="FORM No."
            placeholder="e.g. ICPL/QAD/… ISSUE-01, REV …"
            value={form.formNumber}
            onChange={(e) => set('formNumber', e.target.value)}
          />
          <Box sx={{ textAlign: 'center', border: '1px solid', borderColor: 'divider', p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle1">{FORM_TITLE}</Typography>
          </Box>

          {loading && <LinearProgress />}
          {tlbAutofillNote ? (
            <Typography variant="caption" color="text.secondary">
              {tlbAutofillNote}
            </Typography>
          ) : null}

          <Typography variant="subtitle2" color="primary">
            Aircraft & work order
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Operator name"
                value={form.operatorName}
                onChange={(e) => set('operatorName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date"
                InputLabelProps={{ shrink: true }}
                value={form.entryDate}
                onChange={(e) => set('entryDate', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Aircraft (type / model)"
                value={form.aircraftType}
                onChange={(e) => set('aircraftType', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Registration"
                value={form.aircraftRegistration}
                onChange={(e) => set('aircraftRegistration', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Serial number"
                value={form.aircraftSerialNumber}
                onChange={(e) => set('aircraftSerialNumber', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Work order number"
                value={form.workOrderNumber}
                onChange={(e) => set('workOrderNumber', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Work order date"
                InputLabelProps={{ shrink: true }}
                value={form.workOrderDate}
                onChange={(e) => set('workOrderDate', e.target.value)}
              />
            </Grid>
          </Grid>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Details of maintenance carried out"
            value={form.maintenanceDetails}
            onChange={(e) => set('maintenanceDetails', e.target.value)}
          />

          <Divider />
          <Typography variant="subtitle2" color="primary">
            Total carried forward (at release)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Airframe hrs / Hobbs"
                value={form.aircraftHours}
                onChange={(e) => set('aircraftHours', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Landings"
                value={form.aircraftLandings}
                onChange={(e) => set('aircraftLandings', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Engine #1 serial"
                value={form.engine1Serial}
                onChange={(e) => set('engine1Serial', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Engine #2 serial"
                value={form.engine2Serial}
                onChange={(e) => set('engine2Serial', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Engine #1 hrs"
                value={form.engine1Hours}
                onChange={(e) => set('engine1Hours', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Engine #2 hrs"
                value={form.engine2Hours}
                onChange={(e) => set('engine2Hours', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="LH engine cycles CT"
                value={form.lhEngineCyclesCT}
                onChange={(e) => set('lhEngineCyclesCT', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="LH engine cycles PT"
                value={form.lhEngineCyclesPT}
                onChange={(e) => set('lhEngineCyclesPT', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="LH engine cycles IMP"
                value={form.lhEngineCyclesIMP}
                onChange={(e) => set('lhEngineCyclesIMP', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="RH engine cycles CT"
                value={form.rhEngineCyclesCT}
                onChange={(e) => set('rhEngineCyclesCT', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="RH engine cycles PT"
                value={form.rhEngineCyclesPT}
                onChange={(e) => set('rhEngineCyclesPT', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="RH engine cycles IMP"
                value={form.rhEngineCyclesIMP}
                onChange={(e) => set('rhEngineCyclesIMP', e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider />
          <Typography variant="subtitle2" color="primary">
            Parts, tools & man-hours
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Parts & materials used"
            value={form.partsAndMaterialsUsed}
            onChange={(e) => set('partsAndMaterialsUsed', e.target.value)}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Tools / equipment used"
            value={form.toolsUsed}
            onChange={(e) => set('toolsUsed', e.target.value)}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="AME man-hours"
                value={form.ameManHours}
                onChange={(e) => set('ameManHours', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Technician man-hours"
                value={form.techManHours}
                onChange={(e) => set('techManHours', e.target.value)}
              />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Work accomplished (summary)"
            value={form.workAccomplished}
            onChange={(e) => set('workAccomplished', e.target.value)}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Remarks / observations"
            value={form.remarks}
            onChange={(e) => set('remarks', e.target.value)}
          />

          <Divider />
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Typography variant="subtitle2" color="primary">
              Serialized component removal / installation
            </Typography>
            <Button size="small" startIcon={<Add />} variant="outlined" onClick={addSerializedRow}>
              Add row
            </Button>
          </Stack>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ fontWeight: 600, py: 1.25 }}>
                    {SERIALIZED_RECORD_TITLE}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell rowSpan={2} sx={{ fontWeight: 600, verticalAlign: 'middle', minWidth: 120 }}>
                    Description
                  </TableCell>
                  <TableCell colSpan={2} align="center" sx={{ fontWeight: 600 }}>
                    Off Details.
                  </TableCell>
                  <TableCell colSpan={2} align="center" sx={{ fontWeight: 600 }}>
                    On Details
                  </TableCell>
                  <TableCell rowSpan={2} sx={{ fontWeight: 600, verticalAlign: 'middle', minWidth: 160 }}>
                    {REGULATORY_TRACKING_HEADER}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, minWidth: 88 }}>P/No.</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 88 }}>S/No.</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 88 }}>P/No.</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 88 }}>S/No.</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {serializedRows.map((row, index) => (
                  <TableRow key={`serialized-${index}`}>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder="Description"
                        value={row.description}
                        onChange={(e) => setSerializedRow(index, 'description', e.target.value)}
                        sx={cellFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder="P/No."
                        value={row.offPartNumber}
                        onChange={(e) => setSerializedRow(index, 'offPartNumber', e.target.value)}
                        sx={cellFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder="S/No."
                        value={row.offSerialNumber}
                        onChange={(e) => setSerializedRow(index, 'offSerialNumber', e.target.value)}
                        sx={cellFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder="P/No."
                        value={row.onPartNumber}
                        onChange={(e) => setSerializedRow(index, 'onPartNumber', e.target.value)}
                        sx={cellFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder="S/No."
                        value={row.onSerialNumber}
                        onChange={(e) => setSerializedRow(index, 'onSerialNumber', e.target.value)}
                        sx={cellFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5 }}>
                      <Stack direction="row" alignItems="flex-start" spacing={0.25}>
                        <TextField
                          fullWidth
                          size="small"
                          variant="standard"
                          placeholder="Tracking no. & date"
                          value={row.regulatoryTracking}
                          onChange={(e) => setSerializedRow(index, 'regulatoryTracking', e.target.value)}
                          sx={cellFieldSx}
                        />
                        <IconButton
                          size="small"
                          aria-label="Remove row"
                          onClick={() => removeSerializedRow(index)}
                          disabled={serializedRows.length <= 1}
                          sx={{ mt: 0.25 }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Typography variant="subtitle2" color="primary">
              Spares / consumables used
            </Typography>
            <Button size="small" startIcon={<Add />} variant="outlined" onClick={addSparesConsumablesRow}>
              Add row
            </Button>
          </Stack>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ fontWeight: 600, py: 1.25 }}>
                    {SPARES_CONSUMABLES_TITLE}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ fontWeight: 600 }}>
                    SPARES DETAILS
                  </TableCell>
                  <TableCell colSpan={3} align="center" sx={{ fontWeight: 600 }}>
                    CONSUMABLES DETAILS
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Part No. or Equivalent No.</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 48 }}>Qty.</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 88 }}>Expiry (If applicable)</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Part No. or Equivalent No.</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 88 }}>Expiry (If applicable)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sparesConsumablesRows.map((row, index) => (
                  <TableRow key={`spares-consumables-${index}`}>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder="Description"
                        value={row.spareDescription}
                        onChange={(e) => setSparesConsumablesRow(index, 'spareDescription', e.target.value)}
                        sx={cellFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder="Part no."
                        value={row.sparePartNumber}
                        onChange={(e) => setSparesConsumablesRow(index, 'sparePartNumber', e.target.value)}
                        sx={cellFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5, maxWidth: 64 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder="Qty."
                        value={row.spareQty}
                        onChange={(e) => setSparesConsumablesRow(index, 'spareQty', e.target.value)}
                        sx={cellFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder="Expiry"
                        value={row.spareExpiry}
                        onChange={(e) => setSparesConsumablesRow(index, 'spareExpiry', e.target.value)}
                        sx={cellFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder="Description"
                        value={row.consumableDescription}
                        onChange={(e) => setSparesConsumablesRow(index, 'consumableDescription', e.target.value)}
                        sx={cellFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder="Part no."
                        value={row.consumablePartNumber}
                        onChange={(e) => setSparesConsumablesRow(index, 'consumablePartNumber', e.target.value)}
                        sx={cellFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5 }}>
                      <Stack direction="row" alignItems="flex-start" spacing={0.25}>
                        <TextField
                          fullWidth
                          size="small"
                          variant="standard"
                          placeholder="Expiry"
                          value={row.consumableExpiry}
                          onChange={(e) => setSparesConsumablesRow(index, 'consumableExpiry', e.target.value)}
                          sx={cellFieldSx}
                        />
                        <IconButton
                          size="small"
                          aria-label="Remove row"
                          onClick={() => removeSparesConsumablesRow(index)}
                          disabled={sparesConsumablesRows.length <= 1}
                          sx={{ mt: 0.25 }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Typography variant="subtitle2" color="primary">
              Tools / special tools used
            </Typography>
            <Button size="small" startIcon={<Add />} variant="outlined" onClick={addToolsSpecialRow}>
              Add row
            </Button>
          </Stack>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ fontWeight: 600, py: 1.25 }}>
                    {TOOLS_SPECIAL_TITLE}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>
                    Identification No. (P/N Or S/No)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 88 }}>Range (If applicable)</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Calibration Error (If applicable)</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Calibration Validity (if any)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {toolsSpecialRows.map((row, index) => (
                  <TableRow key={`tools-special-${index}`}>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder="Description"
                        value={row.description}
                        onChange={(e) => setToolsSpecialRow(index, 'description', e.target.value)}
                        sx={cellFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder="P/N or S/No"
                        value={row.identificationNo}
                        onChange={(e) => setToolsSpecialRow(index, 'identificationNo', e.target.value)}
                        sx={cellFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder="Range"
                        value={row.range}
                        onChange={(e) => setToolsSpecialRow(index, 'range', e.target.value)}
                        sx={cellFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder="Calibration error"
                        value={row.calibrationError}
                        onChange={(e) => setToolsSpecialRow(index, 'calibrationError', e.target.value)}
                        sx={cellFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', p: 0.5 }}>
                      <Stack direction="row" alignItems="flex-start" spacing={0.25}>
                        <TextField
                          fullWidth
                          size="small"
                          variant="standard"
                          placeholder="Validity"
                          value={row.calibrationValidity}
                          onChange={(e) => setToolsSpecialRow(index, 'calibrationValidity', e.target.value)}
                          sx={cellFieldSx}
                        />
                        <IconButton
                          size="small"
                          aria-label="Remove row"
                          onClick={() => removeToolsSpecialRow(index)}
                          disabled={toolsSpecialRows.length <= 1}
                          sx={{ mt: 0.25 }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />
          <Typography variant="subtitle2" color="primary">
            Certificate of release to service
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="CRS number"
                value={form.crsNumber}
                onChange={(e) => set('crsNumber', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="CRS date"
                InputLabelProps={{ shrink: true }}
                value={form.crsDate}
                onChange={(e) => set('crsDate', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Name of AMO"
                value={form.amoName}
                onChange={(e) => set('amoName', e.target.value)}
              />
            </Grid>
          </Grid>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {CRS_TEXT}
          </Typography>

          <Divider />
          <Typography variant="subtitle2" color="primary">
            Sign-off
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Performed by (name)"
                value={form.performedByName}
                onChange={(e) => set('performedByName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Auth / licence no."
                value={form.performedByAuth}
                onChange={(e) => set('performedByAuth', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date"
                InputLabelProps={{ shrink: true }}
                value={form.performedByDate}
                onChange={(e) => set('performedByDate', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                label="Place"
                value={form.performedByPlace}
                onChange={(e) => set('performedByPlace', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Inspected / released by (name)"
                value={form.inspectedByName}
                onChange={(e) => set('inspectedByName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Inspector auth / licence no."
                value={form.inspectedByAuth}
                onChange={(e) => set('inspectedByAuth', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Inspector date"
                InputLabelProps={{ shrink: true }}
                value={form.inspectedByDate}
                onChange={(e) => set('inspectedByDate', e.target.value)}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pdfBusy}>
          Close
        </Button>
        <Button variant="outlined" onClick={handleViewPdf} disabled={pdfBusy || !formSavedAt}>
          {viewingPdf ? 'Opening…' : 'View PDF'}
        </Button>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleDownloadPdf}
          disabled={pdfBusy || !formSavedAt}
        >
          {downloadingPdf ? 'Downloading…' : 'Download PDF'}
        </Button>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleDownloadCrsPdf}
          disabled={pdfBusy || !formSavedAt}
        >
          {downloadingCrsPdf ? 'Downloading…' : 'Download CRS'}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={pdfBusy}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkOrderSecondPageDialog;
